import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { Leaf, MapPin, TrendUp, Plus, SignOut, CreditCard, Info, Lock, FileText, Download, Bell, PencilSimple, Trash, DotsThreeVertical, CheckCircle, CaretDown, CaretUp, Drop, CloudRain, Thermometer, Warning, Plant } from '@phosphor-icons/react';
import { TroubleshootHelpButton } from '../components/TroubleshootHelp';
import { TurfPulseLogoSVG } from '../components/TurfPulseLogo';
import { DepartmentNav } from '../components/DepartmentNav';
import { SEO } from '../components/SEO';
import { LawnCareRules } from '../components/LawnCareRules';
import { ActivityLog } from '../components/ActivityLog';
import { StreakTile } from '../components/StreakTile';
import { DiseasePressureCard } from '../components/DiseasePressureCard';
import { ExploreStrip } from '../components/ExploreStrip';
import { SmartPlanStrip } from '../components/SmartPlanStrip';
import { WeatherIntelligence } from '../components/WeatherIntelligence';
import { SprayPlanner } from '../components/SprayPlanner';
import { WeatherStation } from '../components/WeatherStation';
import { BYOSDashboardTile } from '../components/BYOSDashboardTile';
import { EcowittMoistureTile } from '../components/EcowittMoistureTile';
import ECInsightsCard from '../components/ECInsightsCard';
import ZoneVitalsCard from '../components/ZoneVitalsCard';
import { SmartHoldTile } from '../components/SmartHoldTile';
import { MountWallHQCard } from '../components/MountWallHQCard';
import { CockpitClassDashboardCard } from '../components/CockpitClassDashboardCard';
import { OnboardingChecklist } from '../components/OnboardingChecklist';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import axios from 'axios';
import { toast } from 'sonner';

import { API_URL } from '../config/api';

// GDD Education Modal Component
const GDDInfoModal = ({ open, onClose }) => {
  if (!open) return null;
  
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl flex items-center gap-2">
            <Leaf weight="duotone" className="text-primary" />
            What Are Growing Degree Days?
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-6 py-4">
          {/* Origin Story */}
          <div className="bg-muted/50 rounded-lg p-4 border-l-4 border-primary">
            <h3 className="font-semibold text-lg mb-2">The Origin (1735)</h3>
            <p className="text-muted-foreground">
              Growing Degree Days were first defined by <strong>René Antoine Ferchault de Réaumur</strong>, 
              a French scientist, in 1735. While studying insects and plants, he discovered that 
              development wasn't tied to calendar dates — it was tied to accumulated heat.
            </p>
            <p className="text-sm text-muted-foreground mt-2 italic">
              "Plants don't have calendars. They have thermometers."
            </p>
          </div>

          {/* The Science */}
          <div>
            <h3 className="font-semibold text-lg mb-2">The Science</h3>
            <p className="text-muted-foreground mb-3">
              GDD measures heat accumulation above a base temperature where growth occurs. 
              Each day, we calculate how much "growing energy" your lawn received:
            </p>
            <div className="bg-background border rounded-lg p-4 font-mono text-center">
              <span className="text-primary font-bold">GDD</span> = (Daily High + Daily Low) ÷ 2 − Base Temp
            </div>
            <div className="grid grid-cols-2 gap-4 mt-4">
              <div className="bg-blue-500/10 rounded-lg p-3 border border-blue-500/20">
                <p className="font-medium text-blue-700 dark:text-blue-300">Cool-Season Grass</p>
                <p className="text-sm text-muted-foreground">Base: 32°F</p>
                <p className="text-xs text-muted-foreground mt-1">Kentucky Bluegrass, Fescue, Ryegrass</p>
              </div>
              <div className="bg-orange-500/10 rounded-lg p-3 border border-orange-500/20">
                <p className="font-medium text-orange-700 dark:text-orange-300">Warm-Season Grass</p>
                <p className="text-sm text-muted-foreground">Base: 50°F</p>
                <p className="text-xs text-muted-foreground mt-1">Bermuda, St. Augustine, Zoysia</p>
              </div>
            </div>
          </div>

          {/* Why It Matters */}
          <div>
            <h3 className="font-semibold text-lg mb-2">Why It Matters for Your Lawn</h3>
            <div className="space-y-3">
              <div className="flex gap-3">
                <div className="bg-green-500/20 text-green-700 dark:text-green-300 rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold shrink-0">1</div>
                <p className="text-muted-foreground">
                  <strong>Crabgrass Prevention:</strong> Pre-emergent herbicides must be applied before soil reaches 55°F for several consecutive days (~150 GDD). Miss this window, and crabgrass wins.
                </p>
              </div>
              <div className="flex gap-3">
                <div className="bg-green-500/20 text-green-700 dark:text-green-300 rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold shrink-0">2</div>
                <p className="text-muted-foreground">
                  <strong>PGR Timing:</strong> Plant Growth Regulators last ~200-300 GDD, not "4 weeks." A cool spell extends effectiveness; a heat wave shortens it.
                </p>
              </div>
              <div className="flex gap-3">
                <div className="bg-green-500/20 text-green-700 dark:text-green-300 rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold shrink-0">3</div>
                <p className="text-muted-foreground">
                  <strong>Pest Emergence:</strong> Grubs, chinch bugs, and other pests emerge at predictable GDD thresholds — not calendar dates.
                </p>
              </div>
            </div>
          </div>

          {/* Cumulative Tracking */}
          <div className="bg-primary/10 rounded-lg p-4">
            <h3 className="font-semibold text-lg mb-2">Cumulative Tracking (Jan 1 Start)</h3>
            <p className="text-muted-foreground">
              TurfPulse tracks GDD from <strong>January 1st</strong> each year — the standard agronomic practice. 
              This cumulative total helps predict:
            </p>
            <ul className="mt-2 space-y-1 text-sm text-muted-foreground">
              <li>• When weeds will germinate</li>
              <li>• When insects become active</li>
              <li>• When disease pressure peaks</li>
              <li>• Optimal timing for every application</li>
            </ul>
          </div>

          {/* Who Uses This */}
          <div>
            <h3 className="font-semibold text-lg mb-2">Who Else Uses GDD?</h3>
            <p className="text-muted-foreground">
              Golf course superintendents, MLB groundskeepers, university turf programs, and commercial 
              agriculture have relied on GDD for decades. TurfPulse brings this professional-grade 
              science to your backyard.
            </p>
          </div>

          {/* Disclaimer */}
          <div className="text-xs text-muted-foreground/70 bg-muted/30 p-3 rounded">
            <strong>Note:</strong> GDD values may vary ±3% from other sources due to weather station differences. 
            Weather data is provided by third-party sources; accuracy is not guaranteed. 
            This tool is for informational purposes and is not a substitute for professional advice.
          </div>

          {/* CTA */}
          <div className="text-center pt-4 border-t">
            <p className="text-sm text-muted-foreground">
              Want to dive deeper? Visit the <strong>Learn</strong> tab for sprayer calibration guides and more.
            </p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};


export const DashboardPage = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation(['dashboard', 'common', 'nav']);
  const [loading, setLoading] = useState(false);
  const [locationSet, setLocationSet] = useState(false);
  const [currentWeather, setCurrentWeather] = useState(null);
  const [forecast, setForecast] = useState([]);
  const [gddData, setGddData] = useState(null);
  const [gddError, setGddError] = useState(false);
  const [applications, setApplications] = useState([]);
  const [applicationsData, setApplicationsData] = useState({ hidden_count: 0, is_limited: false, upgrade_message: null });
  const [showLocationDialog, setShowLocationDialog] = useState(false);
  const [showApplicationDialog, setShowApplicationDialog] = useState(false);
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);
  const [showGDDInfo, setShowGDDInfo] = useState(false);
  const [showTrackerDialog, setShowTrackerDialog] = useState(false);
  const [availableModels, setAvailableModels] = useState([]);
  const [userTrackers, setUserTrackers] = useState([]);
  const [ytdBaseTemp, setYtdBaseTemp] = useState(50); // Default to 50°F base for YTD
  const [soilTemp, setSoilTemp] = useState(null);
  const [weatherDetailOpen, setWeatherDetailOpen] = useState(false);
  const [lawnConditions, setLawnConditions] = useState(null);
  const [waterInsight, setWaterInsight] = useState(null);
  
  // Reference GDD Comparison
  const [showRefGddDialog, setShowRefGddDialog] = useState(false);
  const [refGddComparison, setRefGddComparison] = useState(null);
  const [refGddForm, setRefGddForm] = useState({ source: 'greencast', base_temp: 50, value: '', notes: '' });
  
  // Application form
  const [productName, setProductName] = useState('');
  const [productType, setProductType] = useState('PGR');
  const [applicationRate, setApplicationRate] = useState('');
  const [notes, setNotes] = useState('');

  // GDD Milestone Notifications
  const GDD_MILESTONES = [
    { gdd: 100, title: "100 GDD Reached", body: "Pre-emergent window is approaching. Start monitoring soil temps closely." },
    { gdd: 150, title: "150 GDD — Crabgrass Alert", body: "Crabgrass germination begins now. If you haven't applied pre-emergent, do it TODAY." },
    { gdd: 200, title: "200 GDD — Pre-Emergent Deadline", body: "Last chance for pre-emergent. After this, switch to post-emergent strategy." },
    { gdd: 350, title: "350 GDD — PGR Season Starts", body: "Time to start your Plant Growth Regulator program. First app of the season." },
    { gdd: 500, title: "500 GDD — Nutsedge Emerging", body: "Yellow nutsedge is active. Scout and treat with Certainty or SedgeHammer." },
    { gdd: 700, title: "700 GDD — Grub Prevention Approaching", body: "Start planning your preventive grub control. Acelepryn or Merit should go down before 1000 GDD." },
    { gdd: 1000, title: "1,000 GDD — Peak Summer", body: "Weed and pest pressure at maximum. Stay on top of scouting and spot treatments." },
    { gdd: 1500, title: "1,500 GDD — Late Summer / Fall Prep", body: "Start planning fall pre-emergent for Poa annua. Scout for fall armyworm activity in the evenings." },
    { gdd: 2000, title: "2,000 GDD — Fall Pre-Emergent Window", body: "Apply fall pre-emergent (Prodiamine, Simazine, or Atrazine depending on grass type) when soil temps drop below 70°F. Check for curative grub damage." },
    { gdd: 2500, title: "2,500 GDD — Season Wind-Down", body: "Final fertilizer application. Lower mowing height gradually. Winterize irrigation system." },
  ];

  const checkGDDMilestones = (ytdGdd) => {
    if (!ytdGdd || typeof ytdGdd !== 'number') return;
    // Check if we have notification permission
    if (!('Notification' in window)) return;
    
    const shownKey = `turfpulse_milestones_${new Date().getFullYear()}`;
    const shown = JSON.parse(localStorage.getItem(shownKey) || '[]');
    
    // Find the highest milestone that GDD has passed
    const passedMilestones = GDD_MILESTONES.filter(m => ytdGdd >= m.gdd);
    if (passedMilestones.length === 0) return;
    
    // Only notify for the CURRENT (most recent) milestone, not old ones
    // This prevents "apply pre-emergent" notifications at 1200+ GDD
    const currentMilestone = passedMilestones[passedMilestones.length - 1];
    
    if (!shown.includes(currentMilestone.gdd)) {
      // Mark ALL passed milestones as shown (so we never fire old ones)
      const allPassedGdd = passedMilestones.map(m => m.gdd);
      const updatedShown = [...new Set([...shown, ...allPassedGdd])];
      localStorage.setItem(shownKey, JSON.stringify(updatedShown));
      
      // Show notification if permitted
      if (Notification.permission === 'granted') {
        new Notification(currentMilestone.title, {
          body: currentMilestone.body,
          icon: '/icon-192.png',
          badge: '/icon-192.png',
          tag: `gdd-${currentMilestone.gdd}`,
        });
      }
      
      // Also show in-app toast
      toast.info(currentMilestone.title, { description: currentMilestone.body, duration: 8000 });
    }
  };

  const [notifPermission, setNotifPermission] = useState(
    typeof window !== 'undefined' && 'Notification' in window ? Notification.permission : 'denied'
  );

  const requestNotificationPermission = async () => {
    if (!('Notification' in window)) return;
    if (Notification.permission === 'default') {
      const result = await Notification.requestPermission();
      setNotifPermission(result);
      if (result === 'granted') {
        toast.success('Notifications enabled! You\'ll get alerts at key GDD milestones.');
      }
    } else if (Notification.permission === 'granted') {
      setNotifPermission('granted');
    }
  };

  useEffect(() => {
    if (user && user.latitude && user.longitude) {
      setLocationSet(true);
      fetchDashboardData();
      requestNotificationPermission();
    } else if (user) {
      setShowLocationDialog(true);
    }
  }, [user]);

  const requestLocation = () => {
    console.log('requestLocation called');
    setLoading(true);
    
    if (!navigator.geolocation) {
      console.error('Geolocation not supported');
      toast.error('Geolocation is not supported by your browser');
      setLoading(false);
      return;
    }
    
    console.log('Requesting geolocation...');
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        console.log('Location received:', position.coords);
        const { latitude, longitude } = position.coords;
        try {
          console.log('Saving location to backend...');
          await axios.post(
            `${API_URL}/api/location/update`,
            { latitude, longitude, location_name: `${latitude.toFixed(4)}, ${longitude.toFixed(4)}` },
            { withCredentials: true }
          );
          console.log('Location saved successfully');
          setLocationSet(true);
          setShowLocationDialog(false);
          toast.success('Location set successfully!');
          fetchDashboardData();
        } catch (error) {
          console.error('Failed to save location:', error);
          toast.error('Failed to save location: ' + (error.response?.data?.detail || error.message));
        } finally {
          setLoading(false);
        }
      },
      (error) => {
        console.error('Geolocation error:', error);
        let errorMessage = 'Failed to get location. ';
        switch(error.code) {
          case error.PERMISSION_DENIED:
            errorMessage += 'Permission denied. Please allow location access.';
            break;
          case error.POSITION_UNAVAILABLE:
            errorMessage += 'Location information unavailable.';
            break;
          case error.TIMEOUT:
            errorMessage += 'Request timed out.';
            break;
          default:
            errorMessage += 'Unknown error.';
        }
        toast.error(errorMessage);
        setLoading(false);
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0
      }
    );
  };

  const fetchDashboardData = async () => {
    try {
      const [weatherRes, forecastRes, gddRes, appsRes, trackersRes, modelsRes] = await Promise.all([
        axios.get(`${API_URL}/api/weather/current`, { withCredentials: true }).catch(() => ({ data: null })),
        axios.get(`${API_URL}/api/weather/forecast`, { withCredentials: true }).catch(() => ({ data: { forecast: [] } })),
        axios.get(`${API_URL}/api/gdd/current?ytd_base=${ytdBaseTemp}`, { withCredentials: true }).catch((err) => {
          console.error('GDD fetch failed:', err?.response?.status, err?.response?.data || err?.message);
          return { data: null, _error: err };
        }),
        axios.get(`${API_URL}/api/applications`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/api/my-trackers`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/api/tracker-models`, { withCredentials: true }).catch(() => ({ data: [] }))
      ]);
      
      // Fetch soil temp separately (non-blocking)
      axios.get(`${API_URL}/api/weather/soil-temp`, { withCredentials: true })
        .then(res => setSoilTemp(res.data))
        .catch(() => {}); // Silent fail — soil temp is supplementary
      
      // Fetch lawn conditions (rainfall, soil moisture, watering deficit)
      axios.get(`${API_URL}/api/weather/lawn-conditions`, { withCredentials: true })
        .then(res => setLawnConditions(res.data))
        .catch(() => {}); // Silent fail — supplementary data
      
      // Fetch water insight (sprinkler output vs deficit)
      axios.get(`${API_URL}/api/irrigation/water-insight`, { withCredentials: true })
        .then(res => setWaterInsight(res.data))
        .catch(() => {});
      
      setCurrentWeather(weatherRes.data);
      setForecast(forecastRes.data.forecast);
      setGddData(gddRes.data);
      setGddError(!gddRes.data);
      // Check GDD milestones for notifications
      checkGDDMilestones(gddRes.data?.ytd_gdd);
      const appsData = appsRes.data;
      if (appsData.logs) {
        setApplications(appsData.logs);
        setApplicationsData({
          hidden_count: appsData.hidden_count || 0,
          is_limited: appsData.is_limited || false,
          upgrade_message: appsData.upgrade_message
        });
      } else {
        // Backwards compatibility
        setApplications(Array.isArray(appsData) ? appsData : []);
      }
      setUserTrackers(trackersRes.data);
      setAvailableModels(modelsRes.data);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      if (error.response?.status === 400) {
        toast.error('Please set your location first');
        setShowLocationDialog(true);
      }
    }
  };

  // Fetch GDD data when base temp changes
  const fetchGDDData = async (baseTemp) => {
    try {
      const gddRes = await axios.get(`${API_URL}/api/gdd/current?ytd_base=${baseTemp}`, { withCredentials: true });
      setGddData(gddRes.data);
      setGddError(false);
    } catch (error) {
      console.error('Error fetching GDD data:', error?.response?.status, error?.response?.data || error?.message);
      setGddError(true);
    }
  };

  // Handle YTD base temp toggle
  const handleYtdBaseTempChange = (newBase) => {
    setYtdBaseTemp(newBase);
    fetchGDDData(newBase);
  };

  // Fetch Reference GDD Comparison
  const fetchRefGddComparison = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/gdd/comparison`, { withCredentials: true });
      setRefGddComparison(res.data);
    } catch (error) {
      console.error('Error fetching GDD comparison:', error);
    }
  };

  // Add Reference GDD Entry
  const handleAddRefGdd = async (e) => {
    e.preventDefault();
    if (!refGddForm.value) {
      toast.error('Please enter a GDD value');
      return;
    }
    try {
      await axios.post(`${API_URL}/api/gdd/reference`, {
        source: refGddForm.source,
        base_temp: parseInt(refGddForm.base_temp),
        value: parseFloat(refGddForm.value),
        notes: refGddForm.notes || null
      }, { withCredentials: true });
      toast.success('Reference GDD added!');
      setShowRefGddDialog(false);
      setRefGddForm({ source: 'greencast', base_temp: 50, value: '', notes: '' });
      fetchRefGddComparison();
    } catch (error) {
      toast.error('Failed to add reference GDD');
    }
  };

  // Fetch comparison on mount
  useEffect(() => {
    if (locationSet) {
      fetchRefGddComparison();
    }
  }, [locationSet]);

  const handleLogApplication = async (e) => {
    e.preventDefault();
    try {
      await axios.post(
        `${API_URL}/api/applications`,
        { product_name: productName, product_type: productType, application_rate: applicationRate, notes },
        { withCredentials: true }
      );
      toast.success('Application logged successfully!');
      setShowApplicationDialog(false);
      setProductName('');
      setApplicationRate('');
      setNotes('');
      fetchDashboardData();
    } catch (error) {
      toast.error('Failed to log application');
    }
  };

  // Edit/Delete application log
  const [editingApp, setEditingApp] = useState(null);
  const [editProductName, setEditProductName] = useState('');
  const [editProductType, setEditProductType] = useState('');
  const [editRate, setEditRate] = useState('');
  const [editNotes, setEditNotes] = useState('');
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [deletingAppId, setDeletingAppId] = useState(null);

  const openEditDialog = (app) => {
    setEditingApp(app);
    setEditProductName(app.product_name || '');
    setEditProductType(app.product_type || '');
    setEditRate(app.application_rate || '');
    setEditNotes(app.notes || '');
    setShowEditDialog(true);
  };

  const handleUpdateApplication = async (e) => {
    e.preventDefault();
    if (!editingApp) return;
    try {
      await axios.put(
        `${API_URL}/api/applications/${editingApp.id}`,
        { product_name: editProductName, product_type: editProductType, application_rate: editRate, notes: editNotes },
        { withCredentials: true }
      );
      toast.success('Application updated');
      setShowEditDialog(false);
      setEditingApp(null);
      fetchDashboardData();
    } catch {
      toast.error('Failed to update application');
    }
  };

  const handleDeleteApplication = async (appId) => {
    try {
      await axios.delete(`${API_URL}/api/applications/${appId}`, { withCredentials: true });
      toast.success('Application discarded');
      setDeletingAppId(null);
      fetchDashboardData();
    } catch {
      toast.error('Failed to delete application');
    }
  };

  const handleUpgrade = async (packageId) => {
    try {
      const origin = window.location.origin;
      const { data } = await axios.post(
        `${API_URL}/api/payments/checkout`,
        { package_id: packageId },
        { withCredentials: true, headers: { origin } }
      );
      window.location.href = data.url;
    } catch (error) {
      toast.error('Failed to create checkout session');
    }
  };

  const handleActivateTracker = async (modelId) => {
    try {
      await axios.post(
        `${API_URL}/api/my-trackers`,
        { model_id: modelId },
        { withCredentials: true }
      );
      toast.success('Tracker activated!');
      setShowTrackerDialog(false);
      fetchDashboardData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to activate tracker');
    }
  };

  const handleResetTracker = async (trackerId) => {
    try {
      await axios.post(
        `${API_URL}/api/my-trackers/${trackerId}/reset`,
        {},
        { withCredentials: true }
      );
      toast.success('Tracker reset successfully!');
      fetchDashboardData();
    } catch (error) {
      toast.error('Failed to reset tracker');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'too_early': return 'bg-muted text-muted-foreground';
      case 'approaching': return 'bg-yellow-100 text-yellow-800';
      case 'target_window': return 'bg-green-100 text-green-800';
      case 'past_due': return 'bg-red-100 text-red-800';
      default: return 'bg-muted text-muted-foreground';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'too_early': return 'Too Early';
      case 'approaching': return 'Approaching Window';
      case 'target_window': return 'Apply Now';
      case 'past_due': return 'Past Due';
      default: return 'Unknown';
    }
  };

  const getCategoryIcon = (category) => {
    switch (category) {
      case 'PGR': return '🌱';
      case 'Weed': return '🌿';
      case 'Pest': return '🐛';
      default: return '⚠️';
    }
  };

  const getCategoryBgColor = (category) => {
    switch (category) {
      case 'PGR': return 'bg-green-100';
      case 'Weed': return 'bg-orange-100';
      case 'Pest': return 'bg-red-100';
      default: return 'bg-purple-100';
    }
  };

  const getProgressBarColor = (status) => {
    switch (status) {
      case 'target_window': return 'bg-green-500';
      case 'approaching': return 'bg-yellow-500';
      case 'past_due': return 'bg-red-500';
      default: return 'bg-gray-300';
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const [zipCode, setZipCode] = useState('');
  const [zipLoading, setZipLoading] = useState(false);

  const handleZipLookup = async (e) => {
    e.preventDefault();
    if (!zipCode || zipCode.length < 5) return;
    setZipLoading(true);
    try {
      const geo = await axios.get(`https://geocoding-api.open-meteo.com/v1/search?name=${encodeURIComponent(zipCode)}&count=1&language=en&format=json`);
      const result = geo.data?.results?.[0];
      if (!result) {
        toast.error('Zip/city not found. Try a nearby city name.');
        setZipLoading(false);
        return;
      }
      await axios.post(
        `${API_URL}/api/location/update`,
        { latitude: result.latitude, longitude: result.longitude, location_name: `${result.name}${result.admin1 ? ', ' + result.admin1 : ''}` },
        { withCredentials: true }
      );
      setLocationSet(true);
      setShowLocationDialog(false);
      toast.success(`Location set to ${result.name}${result.admin1 ? ', ' + result.admin1 : ''}`);
      fetchDashboardData();
    } catch {
      toast.error('Failed to set location');
    } finally {
      setZipLoading(false);
    }
  };

  if (!locationSet) {
    return (
      <div data-testid="dashboard-container" className="min-h-screen bg-background">
        <DepartmentNav />
        <div className="flex items-center justify-center p-8" style={{ minHeight: 'calc(100vh - 120px)' }}>
          <Card className="max-w-md w-full">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MapPin weight="duotone" size={32} className="text-primary" />
                Set Your Location
              </CardTitle>
              <CardDescription>
                Enable location access for accurate GDD tracking and weather data for your area.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <Button 
                data-testid="enable-location-btn"
                onClick={requestLocation} 
                disabled={loading} 
                className="w-full"
              >
                {loading ? 'Getting Location...' : 'Enable Location Access'}
              </Button>
              
              <div className="relative flex items-center gap-2 py-1">
                <div className="flex-1 h-px bg-border" />
                <span className="text-xs text-muted-foreground">or enter manually</span>
                <div className="flex-1 h-px bg-border" />
              </div>
              
              <form onSubmit={handleZipLookup} className="flex gap-2">
                <Input
                  data-testid="zip-code-input"
                  placeholder="Zip code or city name"
                  value={zipCode}
                  onChange={(e) => setZipCode(e.target.value)}
                  className="flex-1"
                />
                <Button 
                  data-testid="zip-submit-btn"
                  type="submit" 
                  disabled={zipLoading || zipCode.length < 2}
                  variant="outline"
                >
                  {zipLoading ? '...' : 'Set'}
                </Button>
              </form>
              
              <Button
                data-testid="skip-location-btn"
                variant="ghost"
                className="w-full text-muted-foreground"
                onClick={() => {
                  setShowLocationDialog(false);
                  setLocationSet(true);
                }}
              >
                Skip for now
              </Button>
              <p className="text-xs text-muted-foreground text-center">
                You can enable location later from the dashboard. Some features require location access.
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  const accumulatedGddData = forecast.map((day, idx) => ({
    date: new Date(day.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
    gdd: forecast.slice(0, idx + 1).reduce((acc, d) => acc + d.gdd, gddData?.accumulated_gdd || 0)
  }));

  return (
    <div data-testid="dashboard-container" className="min-h-screen bg-background">
      <SEO
        title="Dashboard — GDD Tracker"
        description="Your real-time Growing Degree Days dashboard. Track YTD GDD, application timing, and lawn care metrics."
        path="/dashboard"
        noindex={true}
      />
      {/* Department Navigation */}
      <DepartmentNav />

      {/* Mini Explore Strip */}
      <ExploreStrip />

      <main id="main-content" className="max-w-7xl mx-auto p-3 sm:p-6 space-y-4 sm:space-y-6">
        {/* First-login activation checklist — auto-hides when 4/5 done or dismissed */}
        <OnboardingChecklist />
        {/* Smart Plan Strip */}
        <SmartPlanStrip />
        {/* Upgrade Banner */}
        {user?.subscription_tier === 'free' && user?.role !== 'admin' && (
          <Card className="border-green-500/50 bg-gradient-to-r from-green-50 to-blue-50 dark:from-green-950/20 dark:to-blue-950/20">
            <CardContent className="p-4">
              <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                <div className="text-center sm:text-left">
                  <p className="font-semibold text-foreground">Unlock all TurfPulse features!</p>
                  <p className="text-sm text-muted-foreground">Get access to all departments, soil analysis, and spray forecast.</p>
                </div>
                <Button 
                  onClick={() => setShowPaymentDialog(true)} 
                  className="bg-green-600 hover:bg-green-700 whitespace-nowrap"
                  data-testid="upgrade-banner-btn"
                >
                  View Plans
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Notification Permission Prompt */}
        {'Notification' in window && notifPermission !== 'denied' && (
          <div className="flex items-center gap-3 p-3 rounded-lg bg-primary/5 border border-primary/20" data-testid="notification-prompt">
            <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
              <Bell weight="duotone" size={16} className="text-primary" />
            </div>
            <p className="text-sm text-muted-foreground flex-1">
              Get notified at key GDD milestones — crabgrass alert, grub prevention, season changes.
            </p>
            {notifPermission === 'granted' ? (
              <span className="text-sm font-medium text-primary flex items-center gap-1 flex-shrink-0" data-testid="notifications-enabled-badge">
                <CheckCircle weight="bold" size={16} /> Enabled
              </span>
            ) : (
              <Button variant="outline" size="sm" onClick={requestNotificationPermission} data-testid="enable-notifications-btn" className="flex-shrink-0">
                Enable
              </Button>
            )}
          </div>
        )}

        {/* Having Issues? Help Link */}
        <div className="flex justify-end">
          <TroubleshootHelpButton />
        </div>

        {/* Wall HQ — mount on iPad/tablet */}
        <MountWallHQCard />

        {/* Super Cockpit Class — weekly tutorial */}
        <CockpitClassDashboardCard />

        {/* Current Weather & GDD Status */}
        <div className="grid md:grid-cols-3 gap-3 sm:gap-6">
          <Card data-testid="current-weather-card">
            <CardHeader>
              <CardTitle className="text-lg">{t('dashboard:conditions', 'Current Weather')}</CardTitle>
            </CardHeader>
            <CardContent>
              {currentWeather ? (
                <div>
                  <div className="text-4xl font-bold mb-2">{Math.round(currentWeather.temp)}°F</div>
                  <p className="text-muted-foreground">{currentWeather.description}</p>
                  <p className="text-sm text-muted-foreground mt-2">
                    High: {Math.round(currentWeather.temp_max)}° / Low: {Math.round(currentWeather.temp_min)}°
                  </p>
                  {/* Soil Temperature */}
                  {soilTemp && (
                    <div className="mt-3 pt-3 border-t border-border/50" data-testid="soil-temp-widget">
                      <div className="flex items-center justify-between">
                        <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Soil Temp</span>
                        <span className="text-[10px] text-muted-foreground">{soilTemp.depth_label}</span>
                      </div>
                      <div className="flex items-baseline gap-2 mt-1">
                        <span className="text-2xl font-bold text-amber-600 dark:text-amber-400">{Math.round(soilTemp.primary_temp_f)}°F</span>
                        <span className="text-xs text-muted-foreground">now</span>
                      </div>
                      <div className="flex items-center gap-3 mt-1">
                        <span className="text-xs text-muted-foreground">24h avg: <strong>{soilTemp.avg_24h_f}°F</strong></span>
                        {soilTemp.avg_24h_f >= 55 && gddData && (gddData.ytd_gdd || gddData.accumulated_gdd || 0) < 250 && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300 font-medium">Pre-emergent window</span>
                        )}
                        {soilTemp.avg_24h_f >= 50 && soilTemp.avg_24h_f < 55 && gddData && (gddData.ytd_gdd || gddData.accumulated_gdd || 0) < 200 && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300 font-medium">Approaching</span>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <p className="text-muted-foreground" role="status" aria-live="polite">{t('common:loading')}</p>
              )}
            </CardContent>
          </Card>

          <Card data-testid="gdd-status-card" className="md:col-span-2">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-lg">{t('dashboard:title', 'GDD Status')}</CardTitle>
                  <CardDescription>{t('dashboard:subtitle', 'Accumulated Growing Degree Days since last application')}</CardDescription>
                </div>
                <Button 
                  variant="ghost" 
                  size="icon" 
                  onClick={() => setShowGDDInfo(true)}
                  className="text-muted-foreground hover:text-primary"
                  aria-label="Learn about Growing Degree Days"
                  data-testid="gdd-info-btn"
                >
                  <Info size={20} weight="duotone" />
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {gddData ? (
                <div>
                  <div className="flex items-start gap-6 mb-4">
                    {/* GDD Progress Ring */}
                    <div className="flex-shrink-0" data-testid="gdd-progress-ring">
                      <svg width="100" height="100" viewBox="0 0 100 100" className="transform -rotate-90">
                        <circle cx="50" cy="50" r="42" fill="none" stroke="currentColor" strokeWidth="6" className="text-muted/30" />
                        <circle
                          cx="50" cy="50" r="42" fill="none"
                          strokeWidth="6"
                          strokeLinecap="round"
                          className="text-primary transition-all duration-1000"
                          strokeDasharray={`${Math.min((gddData.accumulated_gdd / gddData.threshold) * 263.9, 263.9)} 263.9`}
                        />
                      </svg>
                      <div className="absolute mt-[-75px] ml-[14px] w-[72px] text-center">
                        <div className="text-lg font-bold">{Math.min(Math.round((gddData.accumulated_gdd / gddData.threshold) * 100), 100)}%</div>
                        <div className="text-[9px] text-muted-foreground leading-tight">of threshold</div>
                      </div>
                    </div>
                    {/* GDD Numbers */}
                    <div className="flex-1">
                      <div className="text-5xl font-bold text-primary" aria-label={`${Math.round(gddData.accumulated_gdd)} accumulated growing degree days`}>{Math.round(gddData.accumulated_gdd)}</div>
                      <div className="text-muted-foreground pb-2">/ {gddData.threshold} GDD</div>
                    </div>
                  </div>
                  
                  {/* YTD GDD Display with Base Temp Toggle */}
                  {gddData.ytd_gdd !== undefined && (
                    <div className="bg-muted/30 rounded-lg p-3 mb-4">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm text-muted-foreground">{t('dashboard:ytdGDD', 'Year-to-Date GDD')} (Jan 1)</span>
                        <div className="flex items-center gap-1 bg-background rounded-md p-0.5 border">
                          <button
                            onClick={() => handleYtdBaseTempChange(32)}
                            className={`px-2 py-1 text-xs rounded transition-colors ${
                              ytdBaseTemp === 32 
                                ? 'bg-primary text-primary-foreground' 
                                : 'hover:bg-muted text-muted-foreground'
                            }`}
                            data-testid="ytd-base-32-btn"
                          >
                            32°F
                          </button>
                          <button
                            onClick={() => handleYtdBaseTempChange(50)}
                            className={`px-2 py-1 text-xs rounded transition-colors ${
                              ytdBaseTemp === 50 
                                ? 'bg-primary text-primary-foreground' 
                                : 'hover:bg-muted text-muted-foreground'
                            }`}
                            data-testid="ytd-base-50-btn"
                          >
                            50°F
                          </button>
                        </div>
                      </div>
                      <div className="flex items-baseline gap-2">
                        <span className="text-2xl font-bold text-primary">{Math.round(gddData.ytd_gdd)}</span>
                        <span className="text-sm text-muted-foreground">GDD</span>
                        <span className="text-xs text-muted-foreground ml-auto">
                          base {gddData.ytd_base_temp || ytdBaseTemp}°F
                        </span>
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">
                        {ytdBaseTemp === 32 
                          ? 'Standard for cool-season grass growth tracking'
                          : 'Common for pest/weed emergence predictions'
                        }
                      </p>
                      <p className="text-[10px] text-muted-foreground/60 mt-1 italic" data-testid="gdd-accuracy-disclaimer">
                        Values may vary ±3% from other GDD sources due to weather station differences
                      </p>
                      {gddData.data_through && (
                        <p className="text-xs text-muted-foreground mt-1" data-testid="gdd-data-through">
                          Last updated: {new Date(gddData.data_through + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })} &middot; Next update tomorrow
                        </p>
                      )}
                      
                      {/* Reference GDD Comparison */}
                      <div className="mt-3 pt-3 border-t border-border/50">
                        <div className="flex items-center justify-between">
                          <span className="text-xs font-medium text-muted-foreground">Compare to Greencast</span>
                          <Button 
                            size="sm" 
                            variant="ghost" 
                            className="h-6 text-xs"
                            onClick={() => setShowRefGddDialog(true)}
                            data-testid="add-ref-gdd-btn"
                          >
                            <Plus size={12} className="mr-1" />
                            Add Reference
                          </Button>
                        </div>
                        {refGddComparison?.reference ? (
                          <div className="mt-2 flex items-center gap-3 text-sm">
                            <div className="flex items-baseline gap-1">
                              <span className="font-medium text-blue-600 dark:text-blue-400">{refGddComparison.reference.value}</span>
                              <span className="text-xs text-muted-foreground">({refGddComparison.reference.source})</span>
                            </div>
                            <span className="text-muted-foreground">vs</span>
                            <div className="flex items-baseline gap-1">
                              <span className="font-medium text-primary">
                                {refGddComparison.reference.base_temp === 32 
                                  ? refGddComparison.turfpulse?.gdd_32 
                                  : refGddComparison.turfpulse?.gdd_50}
                              </span>
                              <span className="text-xs text-muted-foreground">(TurfPulse)</span>
                            </div>
                            {refGddComparison.difference && (
                              <span className={`text-xs font-medium px-1.5 py-0.5 rounded ${
                                refGddComparison.difference.assessment === 'close' 
                                  ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                                  : 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400'
                              }`}>
                                {refGddComparison.difference.value > 0 ? '+' : ''}{refGddComparison.difference.value} ({refGddComparison.difference.percent}%)
                              </span>
                            )}
                          </div>
                        ) : (
                          <p className="text-xs text-muted-foreground mt-1 italic">
                            No reference data yet. Add Greencast GDD to compare accuracy.
                          </p>
                        )}
                      </div>
                    </div>
                  )}
                  
                  {gddData.ready_for_application ? (
                    <div className="bg-accent/10 border border-accent p-4 rounded-lg" role="alert">
                      <p className="text-accent font-medium">{t('dashboard:readyToApply')}!</p>
                      <p className="text-sm text-muted-foreground mt-1">
                        {t('dashboard:gddExplanation', "You've reached the recommended GDD threshold.")}
                      </p>
                    </div>
                  ) : (
                    <div className="bg-muted p-4 rounded-lg">
                      <p className="text-foreground font-medium">{t('dashboard:notYetReady')}</p>
                      <p className="text-sm text-muted-foreground mt-1">
                        {gddData.days_since_application} {t('dashboard:daysSinceApplication')}
                      </p>
                    </div>
                  )}
                </div>
              ) : (
                <div className="space-y-3">
                  {gddError ? (
                    <>
                      <p className="text-sm text-amber-600 dark:text-amber-400" role="status" data-testid="gdd-error-msg">
                        GDD data couldn't load. Tap retry below or check your connection.
                      </p>
                      <Button size="sm" variant="outline" onClick={() => fetchGDDData(ytdBaseTemp)} data-testid="gdd-retry-btn">
                        Retry
                      </Button>
                    </>
                  ) : (
                    <p className="text-muted-foreground" role="status" aria-live="polite">{t('common:loading')}</p>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Spray Advisory Card */}
        {currentWeather && (
          (() => {
            const wind = currentWeather.wind_speed ?? null;
            const temp = currentWeather.temp ?? null;
            const hum = currentWeather.humidity ?? null;
            const hasWind = wind !== null;
            const hasTemp = temp !== null;
            const hasHum = hum !== null;
            const isRed = (hasWind && wind >= 10) || (hasTemp && (temp > 90 || temp < 40));
            const isAmber = (hasWind && wind >= 7) || (hasHum && hum > 85) || (hasTemp && temp > 85);
            const borderColor = isRed
              ? 'border-red-300 dark:border-red-800 bg-red-50/50 dark:bg-red-950/20'
              : isAmber
                ? 'border-amber-300 dark:border-amber-800 bg-amber-50/50 dark:bg-amber-950/20'
                : 'border-green-300 dark:border-green-800 bg-green-50/50 dark:bg-green-950/20';
            return (
          <Card data-testid="spray-advisory-card" className={`border-2 ${borderColor}`}>
            <CardContent className="pt-5 pb-4">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-lg font-bold">Spray Advisory</span>
                    {isRed ? (
                      <span className="px-2 py-0.5 rounded-full text-xs font-bold bg-red-200 text-red-800 dark:bg-red-900/40 dark:text-red-300" data-testid="spray-status-badge">Not Recommended</span>
                    ) : isAmber ? (
                      <span className="px-2 py-0.5 rounded-full text-xs font-bold bg-amber-200 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300" data-testid="spray-status-badge">Use Caution</span>
                    ) : (
                      <span className="px-2 py-0.5 rounded-full text-xs font-bold bg-green-200 text-green-800 dark:bg-green-900/40 dark:text-green-300" data-testid="spray-status-badge">Safe to Spray</span>
                    )}
                  </div>

                  {/* Active Advisories */}
                  {(isRed || (hasHum && hum > 85)) && (
                    <div className="mb-3">
                      <p className="text-[10px] font-semibold uppercase tracking-wider text-red-600 dark:text-red-400 mb-1">Active Advisories</p>
                      <div className="space-y-1">
                        {hasTemp && temp > 90 && <p className="text-xs text-muted-foreground">High temperature ({Math.round(temp)}°F ≥ 90° limit)</p>}
                        {hasTemp && temp < 40 && <p className="text-xs text-muted-foreground">Low temperature ({Math.round(temp)}°F — too cold for absorption)</p>}
                        {hasWind && wind >= 10 && <p className="text-xs text-muted-foreground">Wind speed {Math.round(wind)} mph exceeds EPA/NIFA 10 mph guideline</p>}
                        {hasHum && hum > 85 && <p className="text-xs text-muted-foreground">High humidity ({Math.round(hum)}%) — increased drift and runoff risk</p>}
                      </div>
                    </div>
                  )}

                  {/* Conditions Grid */}
                  <div className="grid grid-cols-3 gap-2 sm:gap-3">
                    <div>
                      <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-0.5">Wind</p>
                      <p className={`text-sm font-bold ${!hasWind ? 'text-muted-foreground' : wind >= 10 ? 'text-red-600 dark:text-red-400' : wind >= 7 ? 'text-amber-600 dark:text-amber-400' : 'text-green-600 dark:text-green-400'}`}>
                        {hasWind ? `${Math.round(wind)} mph` : '—'}
                      </p>
                      <p className="text-[10px] text-muted-foreground">EPA limit: 10 mph</p>
                    </div>
                    <div>
                      <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-0.5">Temp</p>
                      <p className={`text-sm font-bold ${!hasTemp ? 'text-muted-foreground' : (temp > 90 || temp < 40) ? 'text-red-600 dark:text-red-400' : temp > 85 ? 'text-amber-600 dark:text-amber-400' : 'text-green-600 dark:text-green-400'}`}>
                        {hasTemp ? `${Math.round(temp)}°F` : '—'}
                      </p>
                      <p className="text-[10px] text-muted-foreground">Best: 45-85°F</p>
                    </div>
                    <div>
                      <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-0.5">Humidity</p>
                      <p className={`text-sm font-bold ${!hasHum ? 'text-muted-foreground' : hum > 85 ? 'text-amber-600 dark:text-amber-400' : 'text-green-600 dark:text-green-400'}`}>
                        {hasHum ? `${Math.round(hum)}%` : '—'}
                      </p>
                      <p className="text-[10px] text-muted-foreground">Best: 40-80%</p>
                    </div>
                  </div>
                  <p className="text-[10px] text-muted-foreground mt-2 italic">Per EPA & NIFA guidelines — always read the product label for specific conditions.</p>
                </div>
              </div>
            </CardContent>
          </Card>
            );
          })()
        )}

        {/* Water Budget tile — how much to water, with soil-aware cycle-and-soak */}
        <Card
          data-testid="water-budget-tile"
          className="border-2 border-emerald-500/30 bg-gradient-to-br from-emerald-500/5 to-transparent cursor-pointer hover:border-emerald-500/60 transition-colors"
          onClick={() => navigate('/water-budget')}
        >
          <CardContent className="pt-5 pb-4">
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-lg font-bold">How much to water?</span>
                  <span className="px-2 py-0.5 rounded-full text-xs font-bold bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300">
                    Data-driven
                  </span>
                </div>
                <p className="text-sm text-muted-foreground mb-3 leading-relaxed">
                  Stop guessing with tuna cans. We combine ET loss, measured rainfall from your PWS,
                  your soil's infiltration rate, and your city's restrictions → exact minutes per zone,
                  split into cycle-and-soak runs so water actually absorbs.
                </p>
                <div className="flex flex-wrap gap-2 text-xs">
                  <span className="px-2 py-1 rounded bg-muted text-muted-foreground">ET0 via FAO-56</span>
                  <span className="px-2 py-1 rounded bg-muted text-muted-foreground">Round Rock / Austin presets</span>
                  <span className="px-2 py-1 rounded bg-muted text-muted-foreground">Catch-cup PR test</span>
                  <span className="px-2 py-1 rounded bg-muted text-muted-foreground">Runoff-safe cycles</span>
                </div>
              </div>
              <div className="flex-shrink-0 text-emerald-600 dark:text-emerald-400 text-2xl">→</div>
            </div>
          </CardContent>
        </Card>

        {/* Mobile-optimized: Daily Summary first, Weather Detail collapsible */}
        <div className="flex flex-col gap-4 sm:gap-6">

        {/* Hey, here's what I'm seeing — prioritized first on mobile */}
        <Card data-testid="assistant-card" className="border-2 border-primary/20 bg-gradient-to-r from-primary/5 to-transparent order-first md:order-last">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <Leaf className="h-5 w-5 text-primary" />
              Hey, here's what I'm seeing
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Priority Notifications */}
            <div className="space-y-2">
              {(() => {
                const notifications = [];
                const ytdGdd = gddData?.ytd_gdd || gddData?.accumulated_gdd || 0;
                const dailyGdd = gddData?.daily_gdd || 12;
                
                // P0 - URGENT (Red) - Frost, disease outbreak, overdue treatments
                if (forecast && forecast[0]?.temp_min < 36) {
                  notifications.push({
                    priority: 'P0',
                    color: 'text-red-600',
                    bg: 'bg-red-50 dark:bg-red-950/30',
                    border: 'border-red-200 dark:border-red-800',
                    icon: '🔴',
                    title: 'Frost possible tonight',
                    desc: 'Cover any new seed or tender plants'
                  });
                }
                if (gddData && gddData.accumulated_gdd >= gddData.threshold * 1.15) {
                  notifications.push({
                    priority: 'P0',
                    color: 'text-red-600',
                    bg: 'bg-red-50 dark:bg-red-950/30',
                    border: 'border-red-200 dark:border-red-800',
                    icon: '🔴',
                    title: 'PGR is overdue',
                    desc: `You're ${Math.round(gddData.accumulated_gdd - gddData.threshold)} GDD past threshold`
                  });
                }
                if (currentWeather && currentWeather.humidity != null && currentWeather.humidity > 85 && currentWeather.temp_min > 70) {
                  notifications.push({
                    priority: 'P0',
                    color: 'text-red-600',
                    bg: 'bg-red-50 dark:bg-red-950/30',
                    border: 'border-red-200 dark:border-red-800',
                    icon: '🔴',
                    title: 'Disease-friendly conditions',
                    desc: 'High humidity + warm nights — watch for fungus'
                  });
                }
                
                // P1 - TODAY (Orange) - Good conditions, action windows
                if (currentWeather && currentWeather.wind_speed != null && currentWeather.wind_speed < 8 && forecast && !forecast[0]?.precipitation) {
                  notifications.push({
                    priority: 'P1',
                    color: 'text-orange-600',
                    bg: 'bg-orange-50 dark:bg-orange-950/30',
                    border: 'border-orange-200 dark:border-orange-800',
                    icon: '🟠',
                    title: 'Great spray conditions today',
                    desc: 'Low wind, no rain — ideal for applications'
                  });
                }
                if (gddData && gddData.accumulated_gdd >= gddData.threshold && gddData.accumulated_gdd < gddData.threshold * 1.15) {
                  notifications.push({
                    priority: 'P1',
                    color: 'text-orange-600',
                    bg: 'bg-orange-50 dark:bg-orange-950/30',
                    border: 'border-orange-200 dark:border-orange-800',
                    icon: '🟠',
                    title: "It's PGR time",
                    desc: "You've hit your GDD threshold"
                  });
                }
                
                // P2 - THIS WEEK (Yellow) - Coming soon
                if (gddData && gddData.accumulated_gdd < gddData.threshold && gddData.threshold - gddData.accumulated_gdd < 75) {
                  const daysOut = Math.round((gddData.threshold - gddData.accumulated_gdd) / dailyGdd);
                  notifications.push({
                    priority: 'P2',
                    color: 'text-yellow-700',
                    bg: 'bg-yellow-50 dark:bg-yellow-950/30',
                    border: 'border-yellow-200 dark:border-yellow-800',
                    icon: '🟡',
                    title: 'PGR coming up',
                    desc: `About ${daysOut} day${daysOut !== 1 ? 's' : ''} out based on current temps`
                  });
                }
                if (ytdGdd > 150 && ytdGdd < 250) {
                  notifications.push({
                    priority: 'P2',
                    color: 'text-yellow-700',
                    bg: 'bg-yellow-50 dark:bg-yellow-950/30',
                    border: 'border-yellow-200 dark:border-yellow-800',
                    icon: '🟡',
                    title: 'Pre-emergent window closing',
                    desc: 'Crabgrass germination starting soon'
                  });
                }
                
                // P3 - FYI (Green) - Routine info
                if (forecast && forecast.slice(0, 2).some(day => day.precipitation > 0.1)) {
                  notifications.push({
                    priority: 'P3',
                    color: 'text-green-700',
                    bg: 'bg-green-50 dark:bg-green-950/30',
                    border: 'border-green-200 dark:border-green-800',
                    icon: '🟢',
                    title: 'Skip watering',
                    desc: "Rain's in the forecast"
                  });
                } else if (lawnConditions && lawnConditions.watering.needs_water) {
                  notifications.push({
                    priority: 'P2',
                    color: 'text-yellow-700',
                    bg: 'bg-yellow-50 dark:bg-yellow-950/30',
                    border: 'border-yellow-200 dark:border-yellow-800',
                    icon: '🟡',
                    title: `Lawn needs water (${Math.abs(lawnConditions.watering.deficit_7d)}" deficit)`,
                    desc: `ET loss exceeding rainfall — ${lawnConditions.last_rain.days_ago != null ? `last rain ${lawnConditions.last_rain.days_ago}d ago` : 'no recent rain'}`
                  });
                } else if (currentWeather && currentWeather.temp > 85) {
                  notifications.push({
                    priority: 'P3',
                    color: 'text-green-700',
                    bg: 'bg-green-50 dark:bg-green-950/30',
                    border: 'border-green-200 dark:border-green-800',
                    icon: '🟢',
                    title: 'Hot one today',
                    desc: 'Deep watering in the morning is best'
                  });
                }
                
                if (currentWeather && currentWeather.wind_speed != null && currentWeather.wind_speed < 10 && (!forecast || !forecast[0]?.precipitation)) {
                  notifications.push({
                    priority: 'P3',
                    color: 'text-green-700',
                    bg: 'bg-green-50 dark:bg-green-950/30',
                    border: 'border-green-200 dark:border-green-800',
                    icon: '🟢',
                    title: 'Good mowing conditions',
                    desc: 'Low wind, dry forecast'
                  });
                }
                
                // Sort by priority and take top 3
                const priorityOrder = { 'P0': 0, 'P1': 1, 'P2': 2, 'P3': 3 };
                const sorted = notifications.sort((a, b) => priorityOrder[a.priority] - priorityOrder[b.priority]);
                const topNotifications = sorted.slice(0, 3);
                
                // If nothing urgent, show a friendly message
                if (topNotifications.length === 0) {
                  return (
                    <div className="flex items-center gap-3 p-3 rounded-lg bg-green-50 dark:bg-green-950/30 border border-green-200 dark:border-green-800">
                      <span className="text-xl">👍</span>
                      <p className="text-sm text-green-700 dark:text-green-300">
                        <span className="font-medium">Looking good today</span>
                        <span className="text-green-600 dark:text-green-400"> — no urgent tasks</span>
                      </p>
                    </div>
                  );
                }
                
                return topNotifications.map((notif, idx) => (
                  <div key={idx} className={`flex items-start gap-3 p-3 rounded-lg ${notif.bg} border ${notif.border}`}>
                    <span className="text-lg">{notif.icon}</span>
                    <div>
                      <p className="text-sm">
                        <span className={`font-medium ${notif.color}`}>{notif.title}</span>
                        <span className="text-muted-foreground"> — {notif.desc}</span>
                      </p>
                    </div>
                  </div>
                ));
              })()}
            </div>
            
            {/* Coming Up Section */}
            <div className="pt-3 border-t border-border/50">
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2 flex items-center gap-1">
                📅 Coming Up
              </p>
              <div className="space-y-1.5">
                {(() => {
                  const comingUp = [];
                  const ytdGdd = gddData?.ytd_gdd || gddData?.accumulated_gdd || 0;
                  const dailyGdd = gddData?.daily_gdd || 0;
                  
                  // Don't estimate days if daily GDD accumulation is too low to be meaningful
                  const canEstimateDays = dailyGdd >= 2;
                  
                  // GDD-based upcoming events
                  if (ytdGdd < 150 && canEstimateDays) {
                    const daysTo150 = Math.round((150 - ytdGdd) / dailyGdd);
                    if (daysTo150 <= 60) {
                      comingUp.push(`Pre-emergent window opens in ~${daysTo150} days`);
                    }
                  }
                  if (ytdGdd < 1000 && ytdGdd > 500 && canEstimateDays) {
                    const daysTo1000 = Math.round((1000 - ytdGdd) / dailyGdd);
                    if (daysTo1000 <= 60) {
                      comingUp.push(`Grub prevention window in ~${daysTo1000} days`);
                    }
                  }
                  if (ytdGdd < 2500 && ytdGdd > 1500 && canEstimateDays) {
                    const daysTo2500 = Math.round((2500 - ytdGdd) / dailyGdd);
                    if (daysTo2500 <= 90) {
                      comingUp.push(`Fall pre-emergent for Poa annua in ~${daysTo2500} days`);
                    }
                  }
                  
                  // PGR future applications
                  if (gddData && canEstimateDays && gddData.accumulated_gdd < gddData.threshold - 75) {
                    const daysOut = Math.round((gddData.threshold - gddData.accumulated_gdd) / dailyGdd);
                    if (daysOut > 3 && daysOut < 21) {
                      comingUp.push(`Next PGR application in ~${daysOut} days`);
                    }
                  }
                  
                  // Seasonal reminders based on month
                  const month = new Date().getMonth();
                  if (month === 2 || month === 3) { // March-April
                    comingUp.push('Soil temps rising — watch for green-up');
                  }
                  if (month === 4 || month === 5) { // May-June  
                    comingUp.push('Peak growing season — stay on top of mowing');
                  }
                  if (month === 8 || month === 9) { // Sept-Oct
                    comingUp.push('Overseeding window approaching');
                  }
                  
                  // Take top 2
                  const top2 = comingUp.slice(0, 2);
                  
                  if (top2.length === 0) {
                    return <p className="text-sm text-muted-foreground">No major events on the horizon</p>;
                  }
                  
                  return top2.map((item, idx) => (
                    <p key={idx} className="text-sm text-muted-foreground flex items-center gap-2">
                      <span className="text-primary">•</span> {item}
                    </p>
                  ));
                })()}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Weather Intelligence & Spray Planner — collapsible on mobile */}
        <div className="order-last md:order-first space-y-4 sm:space-y-6">
          <button
            onClick={() => setWeatherDetailOpen(!weatherDetailOpen)}
            className="md:hidden w-full flex items-center justify-between p-3 bg-muted/50 rounded-lg border border-border/50 text-left"
            data-testid="weather-detail-toggle"
          >
            <span className="font-semibold text-sm text-foreground">Weather Intelligence & Spray Planner</span>
            {weatherDetailOpen ? <CaretUp size={18} className="text-muted-foreground" /> : <CaretDown size={18} className="text-muted-foreground" />}
          </button>
          <div className={`space-y-4 sm:space-y-6 ${weatherDetailOpen ? '' : 'hidden md:block'}`}>
            <WeatherIntelligence />
            <BYOSDashboardTile />
            <SmartHoldTile />
            <EcowittMoistureTile />
            <ZoneVitalsCard />
            <ECInsightsCard />
            <SprayPlanner />
            {new Date() >= new Date('2026-05-25') && <WeatherStation />}
          </div>
        </div>
        </div>

        {/* Lawn Conditions — Rainfall, Soil Moisture, Watering Deficit */}
        {lawnConditions && (
          <Card data-testid="lawn-conditions-card">
            <CardHeader className="pb-3">
              <CardTitle className="text-base sm:text-lg flex items-center gap-2">
                <Plant size={20} className="text-green-600 dark:text-green-400" />
                Lawn Conditions
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Frost Alert Banner */}
              {lawnConditions.frost_alert && (
                <div className="flex items-center gap-2 p-2.5 rounded-lg bg-blue-50 dark:bg-blue-950/40 border border-blue-200 dark:border-blue-800" data-testid="frost-alert-banner">
                  <Warning size={18} className="text-blue-600 dark:text-blue-400 shrink-0" />
                  <p className="text-xs sm:text-sm text-blue-800 dark:text-blue-200">
                    <span className="font-semibold">Frost Warning:</span> {lawnConditions.frost_alert.min_temp}°F forecast on {new Date(lawnConditions.frost_alert.date + 'T12:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                  </p>
                </div>
              )}

              {/* Top row: Rainfall + Soil Moisture */}
              <div className="grid grid-cols-2 gap-3">
                {/* Rainfall Summary */}
                <div className="space-y-2" data-testid="rainfall-summary">
                  <div className="flex items-center gap-1.5">
                    <CloudRain size={14} className="text-blue-500" />
                    <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Rainfall</span>
                  </div>
                  <div className="space-y-1">
                    <div className="flex justify-between items-baseline">
                      <span className="text-xs text-muted-foreground">Today</span>
                      <span className="text-sm font-bold">{lawnConditions.rainfall.today}"</span>
                    </div>
                    <div className="flex justify-between items-baseline">
                      <span className="text-xs text-muted-foreground">7-Day</span>
                      <span className="text-sm font-bold">{lawnConditions.rainfall.last_7d}"</span>
                    </div>
                    <div className="flex justify-between items-baseline">
                      <span className="text-xs text-muted-foreground">30-Day</span>
                      <span className="text-sm font-bold">{lawnConditions.rainfall.last_30d}"</span>
                    </div>
                  </div>
                  {lawnConditions.last_rain.days_ago != null && (
                    <p className="text-[10px] text-muted-foreground mt-1">
                      Last rain: {lawnConditions.last_rain.amount}" — {lawnConditions.last_rain.days_ago === 0 ? 'today' : `${lawnConditions.last_rain.days_ago}d ago`}
                    </p>
                  )}
                </div>

                {/* Soil Moisture */}
                <div className="space-y-2" data-testid="soil-moisture-gauge">
                  <div className="flex items-center gap-1.5">
                    <Drop size={14} className="text-cyan-500" />
                    <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Soil Moisture</span>
                  </div>
                  {lawnConditions.soil_moisture.percent != null ? (
                    <div className="space-y-1.5">
                      <div className="flex items-baseline gap-1">
                        <span className="text-2xl font-bold">{lawnConditions.soil_moisture.percent}</span>
                        <span className="text-xs text-muted-foreground">%</span>
                      </div>
                      {/* Moisture bar */}
                      <div className="w-full h-2 rounded-full bg-muted overflow-hidden">
                        <div
                          className={`h-full rounded-full transition-all ${
                            lawnConditions.soil_moisture.status === 'dry' ? 'bg-amber-500' :
                            lawnConditions.soil_moisture.status === 'fair' ? 'bg-yellow-500' :
                            lawnConditions.soil_moisture.status === 'good' ? 'bg-green-500' :
                            lawnConditions.soil_moisture.status === 'moist' ? 'bg-cyan-500' :
                            'bg-blue-500'
                          }`}
                          style={{ width: `${lawnConditions.soil_moisture.percent}%` }}
                        />
                      </div>
                      <p className={`text-xs font-medium capitalize ${
                        lawnConditions.soil_moisture.status === 'dry' ? 'text-amber-600 dark:text-amber-400' :
                        lawnConditions.soil_moisture.status === 'fair' ? 'text-yellow-600 dark:text-yellow-400' :
                        lawnConditions.soil_moisture.status === 'good' ? 'text-green-600 dark:text-green-400' :
                        lawnConditions.soil_moisture.status === 'moist' ? 'text-cyan-600 dark:text-cyan-400' :
                        'text-blue-600 dark:text-blue-400'
                      }`}>
                        {lawnConditions.soil_moisture.status}
                      </p>
                      <p className="text-[10px] text-muted-foreground">Root zone (3-9cm)</p>
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground">Unavailable</p>
                  )}
                </div>
              </div>

              {/* Watering Deficit */}
              <div className="pt-2 border-t border-border/50" data-testid="watering-deficit">
                <div className="flex items-center gap-1.5 mb-2">
                  <Drop size={14} className="text-emerald-500" />
                  <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">7-Day Water Balance</span>
                </div>
                <div className="flex items-center justify-between mb-1.5">
                  <div className="flex items-baseline gap-1">
                    <span className="text-xs text-muted-foreground">ET loss</span>
                    <span className="text-sm font-semibold text-orange-600 dark:text-orange-400">{lawnConditions.watering.et0_7d}"</span>
                  </div>
                  <div className="flex items-baseline gap-1">
                    <span className="text-xs text-muted-foreground">Rain</span>
                    <span className="text-sm font-semibold text-blue-600 dark:text-blue-400">{lawnConditions.watering.rainfall_7d}"</span>
                  </div>
                  <div className="flex items-baseline gap-1">
                    <span className="text-xs text-muted-foreground">Deficit</span>
                    <span className={`text-sm font-bold ${lawnConditions.watering.deficit_7d > 0 ? 'text-red-600 dark:text-red-400' : 'text-green-600 dark:text-green-400'}`}>
                      {lawnConditions.watering.deficit_7d > 0 ? '-' : '+'}{Math.abs(lawnConditions.watering.deficit_7d)}"
                    </span>
                  </div>
                </div>
                {/* Deficit bar visualization */}
                <div className="w-full h-2.5 rounded-full bg-muted overflow-hidden relative">
                  <div className="absolute inset-0 flex">
                    <div className="h-full bg-blue-400 dark:bg-blue-500 rounded-l-full" style={{ width: `${Math.min(lawnConditions.watering.rainfall_7d / Math.max(lawnConditions.watering.et0_7d, 0.01) * 100, 100)}%` }} />
                  </div>
                </div>
                <p className={`text-xs mt-1.5 font-medium ${lawnConditions.watering.needs_water ? 'text-amber-600 dark:text-amber-400' : 'text-green-600 dark:text-green-400'}`}>
                  {lawnConditions.watering.needs_water
                    ? `Your lawn needs ~${Math.abs(lawnConditions.watering.deficit_7d)}" of water this week`
                    : 'Rainfall is meeting your lawn\'s water needs'}
                </p>
              </div>

              {/* Rain Forecast mini strip */}
              {lawnConditions.rain_forecast && lawnConditions.rain_forecast.length > 0 && (
                <div className="pt-2 border-t border-border/50">
                  <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Rain Outlook</span>
                  <div className="flex gap-2 mt-1">
                    {lawnConditions.rain_forecast.map((day, i) => (
                      <div key={i} className="flex-1 text-center p-1.5 rounded bg-muted/50">
                        <p className="text-[10px] text-muted-foreground">
                          {new Date(day.date + 'T12:00').toLocaleDateString('en-US', { weekday: 'short' })}
                        </p>
                        <p className={`text-xs font-bold ${day.amount >= 0.1 ? 'text-blue-600 dark:text-blue-400' : 'text-muted-foreground'}`}>
                          {day.amount > 0 ? `${day.amount}"` : '—'}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Sprinkler vs Deficit Insight */}
              {waterInsight && waterInsight.zone_count > 0 && (
                <div className={`pt-2 border-t border-border/50 p-2.5 rounded-lg ${
                  waterInsight.status === 'covered' ? 'bg-emerald-50 dark:bg-emerald-950/20' :
                  waterInsight.status === 'marginal' ? 'bg-amber-50 dark:bg-amber-950/20' :
                  waterInsight.status === 'deficit' ? 'bg-red-50 dark:bg-red-950/20' :
                  'bg-muted/50'
                }`} data-testid="water-insight">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Sprinkler Coverage</span>
                    <span className={`text-xs font-bold ${
                      waterInsight.status === 'covered' ? 'text-emerald-600 dark:text-emerald-400' :
                      waterInsight.status === 'marginal' ? 'text-amber-600 dark:text-amber-400' :
                      'text-red-600 dark:text-red-400'
                    }`}>{waterInsight.sprinkler_weekly_inches}"/wk</span>
                  </div>
                  <p className={`text-xs font-medium ${
                    waterInsight.status === 'covered' ? 'text-emerald-700 dark:text-emerald-300' :
                    waterInsight.status === 'marginal' ? 'text-amber-700 dark:text-amber-300' :
                    'text-red-700 dark:text-red-300'
                  }`}>{waterInsight.recommendation}</p>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Lawn Care Rules — Bermuda Bible + University Research */}
        <LawnCareRules grassType={user?.grass_type} />

        {/* Streak Tile — gamified summary above the Activity Log */}
        <StreakTile />

        {/* Disease Pressure — Smith-Kerns Dollar Spot + Large Patch */}
        <DiseasePressureCard grassType={user?.grass_type} />

        {/* Pro-grade tools — quick access to Resistance + Soil Test + Spoon-Feed */}
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2" data-testid="pro-grade-quick-links">
          <a href="/resistance" data-testid="qlink-resistance" className="rounded-lg border border-border/60 bg-card p-3 hover:border-primary/40 transition-colors">
            <div className="text-[10px] font-semibold uppercase tracking-wider text-primary mb-0.5">FRAC / HRAC / IRAC</div>
            <div className="text-sm font-semibold">Resistance Rotation</div>
            <div className="text-[11px] text-muted-foreground">Log apps · avoid stacking</div>
          </a>
          <a href="/soil-test" data-testid="qlink-soil" className="rounded-lg border border-border/60 bg-card p-3 hover:border-primary/40 transition-colors">
            <div className="text-[10px] font-semibold uppercase tracking-wider text-primary mb-0.5">Waypoint · Logan Labs</div>
            <div className="text-sm font-semibold">Soil Test Reader</div>
            <div className="text-[11px] text-muted-foreground">PDF → interpretation</div>
          </a>
          <a href="/calculator" data-testid="qlink-spoonfeed" className="rounded-lg border border-border/60 bg-card p-3 hover:border-primary/40 transition-colors">
            <div className="text-[10px] font-semibold uppercase tracking-wider text-primary mb-0.5">0.25 lb N / 1,000</div>
            <div className="text-sm font-semibold">Spoon-Feed Planner</div>
            <div className="text-[11px] text-muted-foreground">Season-long N schedule</div>
          </a>
        </div>

        {/* Activity Log — Mowing, Watering, Maintenance */}
        <ActivityLog />

        {/* GDD Forecast Chart */}
        <Card data-testid="gdd-forecast-chart">
          <CardHeader>
            <CardTitle>{t('dashboard:forecast')}</CardTitle>
            <CardDescription>{t('dashboard:forecastDesc')}</CardDescription>
          </CardHeader>
          <CardContent>
            {accumulatedGddData.length > 0 ? (
              <div className="h-[200px] sm:h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={accumulatedGddData}>
                  <defs>
                    <linearGradient id="gddGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="hsl(105, 25%, 35%)" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="hsl(105, 25%, 35%)" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(105, 15%, 85%)" />
                  <XAxis dataKey="date" stroke="hsl(120, 10%, 40%)" />
                  <YAxis stroke="hsl(120, 10%, 40%)" />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: 'white',
                      border: '1px solid hsl(105, 15%, 85%)',
                      borderRadius: '8px'
                    }}
                  />
                  <Area
                    type="monotone"
                    dataKey="gdd"
                    stroke="hsl(105, 25%, 35%)"
                    strokeWidth={2}
                    fill="url(#gddGradient)"
                  />
                </AreaChart>
              </ResponsiveContainer>
              </div>
            ) : (
              <p className="text-muted-foreground text-center py-8">Loading forecast...</p>
            )}
          </CardContent>
        </Card>

        {/* Multi-Tracker Dashboard */}
        <Card data-testid="multi-tracker-dashboard">
          <CardHeader className="flex flex-row items-center justify-between gap-2">
            <div className="min-w-0">
              <CardTitle className="text-base sm:text-lg">Active GDD Trackers</CardTitle>
              <CardDescription className="text-xs sm:text-sm">Monitor multiple timing windows for PGR, weeds, pests, and diseases</CardDescription>
            </div>
            <Dialog open={showTrackerDialog} onOpenChange={setShowTrackerDialog}>
              <DialogTrigger asChild>
                <Button data-testid="add-tracker-btn" size="sm" className="whitespace-nowrap">
                  <Plus weight="bold" size={16} className="mr-1 sm:mr-2" />
                  <span className="hidden sm:inline">Add Tracker</span>
                  <span className="sm:hidden">Add</span>
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Activate New Tracker</DialogTitle>
                </DialogHeader>
                <div className="grid gap-4 max-h-96 overflow-y-auto">
                  {availableModels.map((model) => (
                    <div
                      key={model.id}
                      className="border border-border rounded-lg p-4 hover:bg-secondary/50 cursor-pointer"
                      onClick={() => handleActivateTracker(model.id)}
                    >
                      <div className="flex items-start gap-3">
                        <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${getCategoryBgColor(model.category)}`}>
                          <span className="text-2xl">{getCategoryIcon(model.category)}</span>
                        </div>
                        <div className="flex-1">
                          <h4 className="font-bold">{model.name}</h4>
                          <p className="text-sm text-muted-foreground">{model.description}</p>
                          <div className="flex gap-2 mt-2">
                            <span className="text-xs bg-secondary px-2 py-1 rounded">{model.category}</span>
                            <span className="text-xs bg-secondary px-2 py-1 rounded">Target: {model.target_gdd} GDD</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </DialogContent>
            </Dialog>
          </CardHeader>
          <CardContent>
            {userTrackers.length > 0 ? (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
                {userTrackers.map((tracker) => (
                  <div key={tracker.id} className="border border-border rounded-lg p-4">
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <h4 className="font-bold text-sm">{tracker.model_name}</h4>
                        <span className={`text-xs px-2 py-1 rounded ${getStatusColor(tracker.status)}`}>
                          {getStatusLabel(tracker.status)}
                        </span>
                      </div>
                      <span className="text-2xl">{getCategoryIcon(tracker.category)}</span>
                    </div>
                    
                    <div className="space-y-2">
                      <div>
                        <div className="flex justify-between text-sm mb-1">
                          <span className="text-muted-foreground">Accumulated GDD</span>
                          <span className="font-bold">{Math.round(tracker.accumulated_gdd)}</span>
                        </div>
                        <div className="w-full bg-muted rounded-full h-2">
                          <div
                            className={`h-2 rounded-full ${getProgressBarColor(tracker.status)}`}
                            style={{
                              width: `${Math.min(100, (tracker.accumulated_gdd / tracker.target_gdd) * 100)}%`
                            }}
                          />
                        </div>
                      </div>
                      
                      <div className="flex justify-between text-xs text-muted-foreground">
                        <span>Target: {tracker.target_gdd} GDD</span>
                        <span>Base: {tracker.base_temp}°F</span>
                      </div>
                      
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleResetTracker(tracker.id)}
                        className="w-full mt-2"
                      >
                        Reset After Application
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <p className="text-muted-foreground mb-4">No active trackers. Add a tracker to get started!</p>
                <Button onClick={() => setShowTrackerDialog(true)}>
                  <Plus weight="bold" size={18} className="mr-2" />
                  Add Your First Tracker
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Application History */}
        <Card data-testid="application-history">
          <CardHeader className="flex flex-row items-center justify-between gap-2">
            <div className="min-w-0">
              <CardTitle className="text-base sm:text-lg">Application History</CardTitle>
              <CardDescription className="text-xs sm:text-sm">Track your PGR and fertilizer applications</CardDescription>
            </div>
            <Dialog open={showApplicationDialog} onOpenChange={setShowApplicationDialog}>
              <DialogTrigger asChild>
                <Button data-testid="log-application-btn" size="sm" className="whitespace-nowrap">
                  <Plus weight="bold" size={16} className="mr-1 sm:mr-2" />
                  <span className="hidden sm:inline">Log Application</span>
                  <span className="sm:hidden">Log</span>
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Log New Application</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleLogApplication} className="space-y-4">
                  <div>
                    <Label>Product Name</Label>
                    <Input
                      data-testid="product-name-input"
                      value={productName}
                      onChange={(e) => setProductName(e.target.value)}
                      required
                    />
                  </div>
                  <div>
                    <Label>Product Type</Label>
                    <select
                      data-testid="product-type-select"
                      value={productType}
                      onChange={(e) => setProductType(e.target.value)}
                      className="w-full border border-input rounded-md px-3 py-2"
                    >
                      <option value="PGR">PGR</option>
                      <option value="Fertilizer">Fertilizer</option>
                      <option value="Herbicide">Herbicide</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                  <div>
                    <Label>Application Rate (optional)</Label>
                    <Input
                      value={applicationRate}
                      onChange={(e) => setApplicationRate(e.target.value)}
                    />
                  </div>
                  <div>
                    <Label>Notes (optional)</Label>
                    <Input
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                    />
                  </div>
                  {/* EPA Disclaimer */}
                  <p className="text-xs text-muted-foreground bg-muted/50 p-2 rounded">
                    Weather conditions will be auto-captured for EPA record-keeping. Always read and follow product label directions. 
                    You are responsible for compliance with all applicable regulations.
                  </p>
                  <Button type="submit" className="w-full">Save Application</Button>
                </form>
              </DialogContent>
            </Dialog>
          </CardHeader>
          <CardContent>
            {applications.length > 0 ? (
              <div className="space-y-4">
                {applications.map((app) => (
                  <div key={app.id} className="border border-border rounded-lg p-4" data-testid={`app-log-${app.id}`}>
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <h4 className="font-bold">{app.product_name}</h4>
                        <p className="text-sm text-muted-foreground">{app.product_type}</p>
                      </div>
                      <div className="flex items-start gap-2">
                        <div className="text-right">
                          <p className="text-sm font-medium">{Math.round(app.gdd_at_application)} GDD</p>
                          <p className="text-xs text-muted-foreground">
                            {new Date(app.applied_at).toLocaleDateString()}
                          </p>
                        </div>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-8 w-8" data-testid={`app-menu-${app.id}`}>
                              <DotsThreeVertical size={18} />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => openEditDialog(app)} data-testid={`app-edit-${app.id}`}>
                              <PencilSimple size={16} className="mr-2" />
                              Edit
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() => setDeletingAppId(app.id)}
                              className="text-red-600 focus:text-red-600"
                              data-testid={`app-delete-${app.id}`}
                            >
                              <Trash size={16} className="mr-2" />
                              Discard
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </div>
                    {/* EPA Weather Data */}
                    {(app.temperature_f || app.wind_speed_mph || app.humidity_percent) && (
                      <div className="flex gap-3 text-xs text-muted-foreground mt-2 pt-2 border-t border-border/50">
                        {app.temperature_f && <span>{Math.round(app.temperature_f)}°F</span>}
                        {app.wind_speed_mph && <span>{Math.round(app.wind_speed_mph)} mph {app.wind_direction || ''}</span>}
                        {app.humidity_percent && <span>{Math.round(app.humidity_percent)}% humidity</span>}
                      </div>
                    )}
                    {app.notes && (
                      <p className="text-sm text-muted-foreground mt-2">{app.notes}</p>
                    )}
                  </div>
                ))}
                {/* Upgrade prompt for free users with hidden logs */}
                {applicationsData.is_limited && applicationsData.hidden_count > 0 && (
                  <div 
                    className="border-2 border-dashed border-amber-500/50 rounded-lg p-4 bg-amber-50/50 dark:bg-amber-950/20 cursor-pointer hover:border-amber-500 transition-colors"
                    onClick={() => setShowPaymentDialog(true)}
                    data-testid="spray-log-upgrade-prompt"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Lock className="h-5 w-5 text-amber-600" />
                        <div>
                          <p className="font-medium text-amber-800 dark:text-amber-200">
                            {applicationsData.hidden_count} more application{applicationsData.hidden_count > 1 ? 's' : ''} hidden
                          </p>
                          <p className="text-xs text-amber-600 dark:text-amber-400">
                            Upgrade to see your full spray history
                          </p>
                        </div>
                      </div>
                      <Button variant="outline" size="sm" className="border-amber-500 text-amber-700 hover:bg-amber-100">
                        Upgrade
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <p className="text-muted-foreground text-center py-8">
                {t('dashboard:noApplications')}
              </p>
            )}
          </CardContent>
        </Card>

        {/* Season Report PDF Export Card */}
        <Card data-testid="season-report-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Season Report
            </CardTitle>
            <CardDescription>Download your branded PDF season report with spray logs and EPA compliance data</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
              <div className="space-y-1">
                <p className="text-sm text-muted-foreground">
                  Includes: Application history, GDD tracking, weather conditions at spray time, EPA compliance summary
                </p>
              </div>
              {user?.subscription_tier === 'enthusiast_annual' || user?.role === 'admin' ? (
                <Button 
                  onClick={async () => {
                    try {
                      // Pre-check: verify there are applications to report on
                      const checkRes = await axios.get(`${API_URL}/api/reports/season-summary`, { withCredentials: true });
                      if (!checkRes.data?.statistics?.total_applications || checkRes.data.statistics.total_applications === 0) {
                        toast.info('No application logs yet this season. Start logging spray applications to generate your report.');
                        return;
                      }
                      const response = await axios.get(`${API_URL}/api/reports/season-pdf`, {
                        withCredentials: true,
                        responseType: 'blob'
                      });
                      const url = window.URL.createObjectURL(new Blob([response.data]));
                      const link = document.createElement('a');
                      link.href = url;
                      link.setAttribute('download', `TurfPulse_Season_Report_${new Date().getFullYear()}.pdf`);
                      document.body.appendChild(link);
                      link.click();
                      link.remove();
                      toast.success('Season report downloaded!');
                    } catch (error) {
                      if (error.response?.status === 403) {
                        toast.error('Season Report PDF requires Enthusiast Annual subscription');
                        setShowPaymentDialog(true);
                      } else {
                        toast.error('Failed to download report');
                      }
                    }
                  }}
                  data-testid="download-season-report-btn"
                >
                  <Download className="h-4 w-4 mr-2" />
                  Download PDF
                </Button>
              ) : (
                <Button 
                  variant="outline" 
                  onClick={() => setShowPaymentDialog(true)}
                  className="border-amber-500 text-amber-700 hover:bg-amber-50"
                  data-testid="season-report-upgrade-btn"
                >
                  <Lock className="h-4 w-4 mr-2" />
                  Enthusiast Only
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      </main>

      {/* Payment Dialog */}
      <Dialog open={showPaymentDialog} onOpenChange={setShowPaymentDialog}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>{t('common:upgrade')}</DialogTitle>
          </DialogHeader>
          <div className="grid md:grid-cols-2 gap-4">
            {/* Homeowner Seasonal */}
            <Card className="border-2 border-green-500 relative overflow-hidden">
              <div className="absolute top-0 right-0 bg-green-500 text-white text-xs px-3 py-1 rounded-bl-lg font-medium">
                POPULAR
              </div>
              <CardHeader>
                <CardTitle className="text-green-700 dark:text-green-400">Homeowner Seasonal</CardTitle>
                <CardDescription>Perfect for lawn season (6 months)</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <div className="text-3xl font-bold">$50<span className="text-lg font-normal text-muted-foreground">/6 mo</span></div>
                  <div className="text-sm text-green-600 font-medium">1 month FREE trial included</div>
                  <div className="text-xs text-muted-foreground">Just $8.33/month</div>
                </div>
                <ul className="space-y-2 text-sm">
                  <li className="flex items-center gap-2">
                    <span className="text-green-500">✓</span> GDD Tracking (unlimited properties)
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="text-green-500">✓</span> PGR & Weed Control Departments
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="text-green-500">✓</span> Equipment Log & Spreader Settings
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="text-green-500">✓</span> Seasonal Calendar & Email Alerts
                  </li>
                </ul>
                <Button onClick={() => handleUpgrade('homeowner_seasonal')} className="w-full bg-green-600 hover:bg-green-700" data-testid="subscribe-seasonal-btn">
                  Start Free Trial
                </Button>
              </CardContent>
            </Card>
            
            {/* Enthusiast Annual */}
            <Card className="border-2 border-blue-500 relative overflow-hidden">
              <div className="absolute top-0 right-0 bg-blue-500 text-white text-xs px-3 py-1 rounded-bl-lg font-medium">
                BEST VALUE
              </div>
              <CardHeader>
                <CardTitle className="text-blue-700 dark:text-blue-400">Enthusiast Annual</CardTitle>
                <CardDescription>Full year, ALL features unlocked</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <div className="text-3xl font-bold">$80<span className="text-lg font-normal text-muted-foreground">/year</span></div>
                  <div className="text-sm text-blue-600 font-medium">2 months FREE trial included</div>
                  <div className="text-xs text-muted-foreground">Just $6.66/month</div>
                </div>
                <ul className="space-y-2 text-sm">
                  <li className="flex items-center gap-2">
                    <span className="text-blue-500">✓</span> Everything in Seasonal, PLUS:
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="text-blue-500">✓</span> Pest & Disease Departments
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="text-blue-500">✓</span> Soil Analysis & Recommendations
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="text-blue-500">✓</span> 5-Day Spray Forecast
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="text-blue-500">✓</span> Unlimited zones & Priority support
                  </li>
                </ul>
                <Button onClick={() => handleUpgrade('enthusiast_annual')} className="w-full bg-blue-600 hover:bg-blue-700" data-testid="subscribe-annual-btn">
                  Start Free Trial
                </Button>
              </CardContent>
            </Card>
          </div>
          <p className="text-xs text-center text-muted-foreground mt-2">
            Cancel anytime during your free trial. No charge until trial ends.
          </p>
        </DialogContent>
      </Dialog>

      {/* Location Dialog */}
      <Dialog open={showLocationDialog} onOpenChange={setShowLocationDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Set Your Location</DialogTitle>
          </DialogHeader>
          <p className="text-muted-foreground mb-4">
            We need your location to provide accurate GDD tracking and weather data.
          </p>
          <Button onClick={requestLocation} disabled={loading} className="w-full">
            {loading ? 'Getting Location...' : 'Enable Location Access'}
          </Button>
        </DialogContent>
      </Dialog>

      {/* GDD Info Modal */}
      <GDDInfoModal open={showGDDInfo} onClose={() => setShowGDDInfo(false)} />

      {/* Reference GDD Dialog */}
      <Dialog open={showRefGddDialog} onOpenChange={setShowRefGddDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <TrendUp size={20} className="text-blue-500" />
              Add Reference GDD
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleAddRefGdd} className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Enter GDD values from external sources (like Greencast emails) to compare with TurfPulse calculations.
            </p>
            
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="ref-source">Source</Label>
                <select
                  id="ref-source"
                  value={refGddForm.source}
                  onChange={(e) => setRefGddForm(f => ({ ...f, source: e.target.value }))}
                  className="w-full mt-1 h-10 px-3 rounded-md border border-input bg-background text-sm"
                >
                  <option value="greencast">Greencast</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <div>
                <Label htmlFor="ref-base">Base Temp</Label>
                <select
                  id="ref-base"
                  value={refGddForm.base_temp}
                  onChange={(e) => setRefGddForm(f => ({ ...f, base_temp: e.target.value }))}
                  className="w-full mt-1 h-10 px-3 rounded-md border border-input bg-background text-sm"
                >
                  <option value="32">32°F</option>
                  <option value="50">50°F</option>
                </select>
              </div>
            </div>
            
            <div>
              <Label htmlFor="ref-value">GDD Value *</Label>
              <Input
                id="ref-value"
                type="number"
                step="0.1"
                placeholder="e.g., 485"
                value={refGddForm.value}
                onChange={(e) => setRefGddForm(f => ({ ...f, value: e.target.value }))}
                required
                data-testid="ref-gdd-value-input"
              />
            </div>
            
            <div>
              <Label htmlFor="ref-notes">Notes (optional)</Label>
              <Input
                id="ref-notes"
                placeholder="e.g., From 4/5/26 email"
                value={refGddForm.notes}
                onChange={(e) => setRefGddForm(f => ({ ...f, notes: e.target.value }))}
              />
            </div>
            
            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => setShowRefGddDialog(false)}>
                Cancel
              </Button>
              <Button type="submit" data-testid="save-ref-gdd-btn">
                Add Entry
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Application Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent data-testid="edit-app-dialog">
          <DialogHeader>
            <DialogTitle>Edit Application Log</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleUpdateApplication} className="space-y-4">
            <div>
              <Label htmlFor="edit-product">Product Name</Label>
              <Input id="edit-product" data-testid="edit-product-name" value={editProductName} onChange={(e) => setEditProductName(e.target.value)} required />
            </div>
            <div>
              <Label htmlFor="edit-type">Product Type</Label>
              <Input id="edit-type" data-testid="edit-product-type" value={editProductType} onChange={(e) => setEditProductType(e.target.value)} />
            </div>
            <div>
              <Label htmlFor="edit-rate">Application Rate</Label>
              <Input id="edit-rate" data-testid="edit-rate" value={editRate} onChange={(e) => setEditRate(e.target.value)} />
            </div>
            <div>
              <Label htmlFor="edit-notes">Notes</Label>
              <Input id="edit-notes" data-testid="edit-notes" value={editNotes} onChange={(e) => setEditNotes(e.target.value)} />
            </div>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setShowEditDialog(false)}>Cancel</Button>
              <Button type="submit" data-testid="save-edit-btn">Save Changes</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deletingAppId} onOpenChange={() => setDeletingAppId(null)}>
        <DialogContent data-testid="delete-confirm-dialog">
          <DialogHeader>
            <DialogTitle>Discard Application Log?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">This will permanently remove this spray log entry. This cannot be undone.</p>
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" onClick={() => setDeletingAppId(null)} data-testid="cancel-delete-btn">Cancel</Button>
            <Button variant="destructive" onClick={() => handleDeleteApplication(deletingAppId)} data-testid="confirm-delete-btn">Discard Entry</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
